import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Users, PoundSterling, TrendingUp, Phone, Clock, Plus,
  ArrowUpRight, ArrowDownRight, Calendar, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import LeadModal from '@/components/LeadModal';
import LeadCard from '@/components/LeadCard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const { selectedCampaign, user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);

  const fetchData = useCallback(async () => {
    if (!selectedCampaign) return;
    
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const [statsRes, leadsRes, followUpsRes] = await Promise.all([
        fetch(`${API_URL}/api/stats/${selectedCampaign.campaign_id}`, { credentials: 'include', headers }),
        fetch(`${API_URL}/api/leads?campaign_id=${selectedCampaign.campaign_id}&limit=10`, { credentials: 'include', headers }),
        fetch(`${API_URL}/api/leads/followups?campaign_id=${selectedCampaign.campaign_id}`, { credentials: 'include', headers })
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (leadsRes.ok) setLeads(await leadsRes.json());
      if (followUpsRes.ok) setFollowUps(await followUpsRes.json());
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  }, [selectedCampaign]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLeadClick = (lead) => {
    setSelectedLead(lead);
    setShowLeadModal(true);
  };

  const handleLeadUpdate = () => {
    fetchData();
    setShowLeadModal(false);
    setSelectedLead(null);
  };

  if (!selectedCampaign) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-700">No Campaign Selected</h2>
          <p className="text-slate-500 mt-2">Please select a campaign from the sidebar to view the dashboard</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-4 border-[#00B4D8] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Dashboard
          </h1>
          <p className="text-slate-500 mt-1">{selectedCampaign.name} Campaign Overview</p>
        </div>
        <Button 
          onClick={() => { setSelectedLead(null); setShowLeadModal(true); }}
          className="bg-[#00B4D8] hover:bg-[#009AC0] text-white"
          data-testid="add-lead-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Lead
        </Button>
      </div>

      {/* Stats Grid */}
      <div className={`grid grid-cols-2 ${isSuperAdmin ? 'lg:grid-cols-4' : 'lg:grid-cols-2'} gap-3 sm:gap-4 lg:gap-6`}>
        <Card className="card-hover" data-testid="stat-total-leads">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-slate-500 font-medium">Total Leads</p>
                <p className="text-2xl sm:text-3xl font-bold text-slate-900 mt-1 sm:mt-2">{stats?.total_leads || 0}</p>
              </div>
              <div className="p-2 sm:p-3 bg-sky-100 rounded-xl flex-shrink-0">
                <Users className="w-5 h-5 sm:w-6 sm:h-6 text-[#00B4D8]" />
              </div>
            </div>
            <div className="mt-3 sm:mt-4 flex items-center gap-2">
              <Badge variant="secondary" className="bg-sky-50 text-[#00B4D8] text-xs">
                {stats?.new_count || 0} new
              </Badge>
            </div>
          </CardContent>
        </Card>

        {isSuperAdmin && (
          <Card className="card-hover" data-testid="stat-revenue">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm text-slate-500 font-medium">Revenue</p>
                  <p className="text-xl sm:text-3xl font-bold text-slate-900 mt-1 sm:mt-2">£{(stats?.total_revenue || 0).toLocaleString()}</p>
                </div>
                <div className="p-2 sm:p-3 bg-emerald-100 rounded-xl flex-shrink-0">
                  <PoundSterling className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600" />
                </div>
              </div>
              <div className="mt-3 sm:mt-4 flex items-center gap-1 text-xs sm:text-sm">
                {stats?.conversion_rate > 0 ? (
                  <>
                    <ArrowUpRight className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-600" />
                    <span className="text-emerald-600 font-medium">{stats?.conversion_rate}%</span>
                  </>
                ) : (
                  <span className="text-slate-500">No conversions</span>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="card-hover" data-testid="stat-won">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-slate-500 font-medium">Won / Lost</p>
                <p className="text-2xl sm:text-3xl font-bold text-slate-900 mt-1 sm:mt-2">
                  <span className="text-emerald-600">{stats?.won_count || 0}</span>
                  <span className="text-slate-400 mx-1">/</span>
                  <span className="text-red-500">{stats?.lost_count || 0}</span>
                </p>
              </div>
              <div className="p-2 sm:p-3 bg-indigo-100 rounded-xl flex-shrink-0">
                <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600" />
              </div>
            </div>
            <div className="mt-3 sm:mt-4 flex items-center gap-2">
              <Badge className="status-qualified text-xs">{stats?.qualified_count || 0} qualified</Badge>
            </div>
          </CardContent>
        </Card>

        {isSuperAdmin && (
          <Card className="card-hover" data-testid="stat-roas">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-500 font-medium">ROAS</p>
                  <p className="text-3xl font-bold text-slate-900 mt-2">{stats?.roas || 0}x</p>
                </div>
                <div className="p-3 bg-amber-100 rounded-xl">
                  <TrendingUp className="w-6 h-6 text-amber-600" />
                </div>
              </div>
              <div className="mt-4 text-sm text-slate-500">
                Avg CPL: £{stats?.avg_cost_per_lead || 0}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Follow-ups Alert */}
      {followUps.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/50" data-testid="followups-alert">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-amber-900">
                  {followUps.length} follow-up{followUps.length > 1 ? 's' : ''} due
                </p>
                <p className="text-sm text-amber-700">You have leads that need callbacks today or are overdue</p>
              </div>
              <Button 
                variant="outline" 
                className="border-amber-300 text-amber-700 hover:bg-amber-100"
                onClick={() => window.location.href = '/followups'}
                data-testid="view-followups-btn"
              >
                View All
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Leads & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Leads */}
        <div className="lg:col-span-2">
          <Card data-testid="recent-leads-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Recent Leads</CardTitle>
                <Button variant="ghost" size="sm" className="text-[#00B4D8]">
                  View All
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {leads.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <Users className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                  <p>No leads yet. Add your first lead!</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {leads.slice(0, 5).map((lead, index) => (
                    <LeadCard
                      key={lead.lead_id}
                      lead={lead}
                      onClick={() => handleLeadClick(lead)}
                      style={{ animationDelay: `${index * 0.05}s` }}
                      showRevenue={isSuperAdmin}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Stats */}
        <div className="space-y-4">
          <Card data-testid="pipeline-summary-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Pipeline Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-sky-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-[#00B4D8]"></div>
                  <span className="text-sm font-medium">New</span>
                </div>
                <span className="font-bold text-slate-900">{stats?.new_count || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                  <span className="text-sm font-medium">Contacted</span>
                </div>
                <span className="font-bold text-slate-900">{stats?.contacted_count || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-indigo-500"></div>
                  <span className="text-sm font-medium">Qualified</span>
                </div>
                <span className="font-bold text-slate-900">{stats?.qualified_count || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                  <span className="text-sm font-medium">Won</span>
                </div>
                <span className="font-bold text-slate-900">{stats?.won_count || 0}</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-red-500"></div>
                  <span className="text-sm font-medium">Lost</span>
                </div>
                <span className="font-bold text-slate-900">{stats?.lost_count || 0}</span>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card data-testid="quick-actions-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button 
                variant="outline" 
                className="w-full justify-start gap-3"
                onClick={() => window.location.href = '/pipeline'}
              >
                <TrendingUp className="w-4 h-4" />
                View Pipeline
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start gap-3"
                onClick={() => window.location.href = '/followups'}
              >
                <Calendar className="w-4 h-4" />
                Check Follow-ups
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start gap-3"
                onClick={() => { setSelectedLead(null); setShowLeadModal(true); }}
              >
                <Plus className="w-4 h-4" />
                Add New Lead
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Lead Modal */}
      <LeadModal 
        isOpen={showLeadModal}
        onClose={() => { setShowLeadModal(false); setSelectedLead(null); }}
        lead={selectedLead}
        campaignId={selectedCampaign?.campaign_id}
        onUpdate={handleLeadUpdate}
      />
    </div>
  );
};

export default Dashboard;
